/*
 * Ogé Exclusive, delivery promise.
 *
 * The Liquid in `snippets/oge-delivery-promise.liquid` renders a correct date
 * at the moment the page is rendered, which is not the moment it is read: an
 * edge-cached product page will happily promise "morgen" at half past eleven at
 * night. This recomputes the same rule against the reader's own clock and
 * rewrites the line in place, so the page degrades to a stale-but-sane date
 * rather than depending on JavaScript to say anything at all.
 *
 * The rule: order before 22:00 on a working day and it ships that day. After
 * the cut-off, or in the weekend, it goes out the next working day. Nothing is
 * delivered on Saturday or Sunday.
 */
(() => {
  /* Alle tekst komt uit het element zelf. Dit script had zijn eigen
     Nederlandse dag- en maandnamen en zijn eigen Nederlandse zinnen, dus op
     /en/, /fr/, /es/ en /el/ schreef het de door Liquid vertaalde regel
     meteen weer terug naar het Nederlands zodra de bezoeker de pagina laadde.
     Liquid zet nu alles klaar in data-attributen; hier staat alleen nog de
     Nederlandse terugval voor het geval een attribuut ontbreekt. */
  const NL_DAYS = 'zondag,maandag,dinsdag,woensdag,donderdag,vrijdag,zaterdag';
  const NL_MONTHS =
    'januari,februari,maart,april,mei,juni,juli,augustus,september,oktober,november,december';

  const list = (value, fallback) => String(value || fallback).split(',');
  const text = (value, fallback) => String(value || fallback);

  /* The shop ships from the Netherlands, so the cut-off is Amsterdam time
     whatever the reader's own timezone happens to be. */
  const shopNow = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Amsterdam',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const get = (type) => Number(parts.find((p) => p.type === type).value);
    /* Constructed as a local date purely to do calendar arithmetic on;
       only the wall-clock fields it was built from are ever read back. */
    return new Date(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
  };

  const deliveryDate = (now, cutoffHour) => {
    const target = new Date(now);
    target.setDate(target.getDate() + (now.getHours() >= cutoffHour ? 2 : 1));

    if (target.getDay() === 6) target.setDate(target.getDate() + 2);
    else if (target.getDay() === 0) target.setDate(target.getDate() + 1);

    return target;
  };

  const render = (root) => {
    const cutoffHour = Number(root.dataset.cutoff) || 22;
    const now = shopNow();
    const target = deliveryDate(now, cutoffHour);

    const dayNode = root.querySelector('[data-oge-delivery-day]');
    const cutoffNode = root.querySelector('[data-oge-delivery-cutoff]');

    const days = list(root.dataset.days, NL_DAYS);
    const months = list(root.dataset.months, NL_MONTHS);
    const cutoffText = text(root.dataset.cutoffText, `bestel vandaag vóór ${cutoffHour}:00`);

    if (dayNode) {
      const date = `${days[target.getDay()]} ${target.getDate()} ${months[target.getMonth()]}`;
      dayNode.textContent = text(root.dataset.arrives, 'In huis op %s').replace('%s', date);
    }

    if (!cutoffNode) return;

    if (now.getHours() >= cutoffHour) {
      cutoffNode.textContent = text(root.dataset.afterCutoff, 'vandaag na sluitingstijd besteld');
      return;
    }

    const minutesLeft = (cutoffHour - now.getHours()) * 60 - now.getMinutes();
    if (minutesLeft <= 0) {
      cutoffNode.textContent = cutoffText;
      return;
    }

    /* A countdown only helps when it is short enough to feel like a reason to
       act; "nog 11 uur" is just noise. */
    if (minutesLeft <= 240) {
      const hours = Math.floor(minutesLeft / 60);
      const minutes = minutesLeft % 60;
      const parts = [];
      if (hours) parts.push(`${hours} ${text(root.dataset.unitHour, 'uur')}`);
      if (minutes) parts.push(`${minutes} ${text(root.dataset.unitMinute, 'min')}`);
      const span = parts.join(` ${text(root.dataset.unitJoin, 'en')} `);
      cutoffNode.textContent = text(root.dataset.timeLeft, 'nog %s om te bestellen').replace(
        '%s',
        span
      );
    } else {
      cutoffNode.textContent = cutoffText;
    }
  };

  const renderAll = () => document.querySelectorAll('[data-oge-delivery]').forEach(render);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAll, { once: true });
  } else {
    renderAll();
  }

  /* The cart drawer and the variant picker both swap their markup in, which
     drops the rewritten text back to whatever Liquid cached. */
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('[data-oge-delivery]')) render(node);
        node.querySelectorAll?.('[data-oge-delivery]').forEach(render);
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  /* Keep the countdown honest for anyone who leaves the tab open. */
  setInterval(renderAll, 60_000);
})();
